import { useEffect, useRef, useState } from "react";
import { PlayCircle, Clock, LockSimple } from "@phosphor-icons/react";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";

export const formatDuration = (s) => {
  if (!s) return "";
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60);
  return h ? `${h}h ${m}m` : `${m} min`;
};

export default function Resources() {
  const [videos, setVideos] = useState(null);
  const [active, setActive] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get(`/resources/videos`).then(({ data }) => setVideos(data.videos)).catch(() => { setVideos([]); setError("Could not load resources"); });
  }, []);

  return (
    <div className="min-h-screen" style={{ background: "var(--background)" }} data-testid="resources-page">
      <Navbar />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8 space-y-6">
        <div>
          <p className="label-uppercase">Library</p>
          <h1 className="font-display text-4xl sm:text-5xl font-bold" style={{ color: "var(--foreground)" }}>Resources</h1>
          <p className="text-sm mt-1" style={{ color: "var(--muted)" }}>Recorded sessions and masterclasses. Videos play here in the hub; your place is saved automatically.</p>
        </div>
        {error && <p className="error-banner" data-testid="resources-error">{error}</p>}
        {active && <Player video={active} onClose={() => setActive(null)} />}
        {videos === null ? (
          <div className="py-16 flex justify-center"><span className="spinner" /></div>
        ) : videos.length === 0 ? (
          <div className="chat-empty" data-testid="resources-empty">
            <p className="font-display font-bold text-lg" style={{ color: "var(--foreground)" }}>No videos yet</p>
            <p className="text-sm">Recordings will appear here once your coach publishes them.</p>
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="resources-grid">
            {videos.map((v) => (
              <button key={v.id} type="button" className={`video-card ${active?.id === v.id ? "is-active" : ""}`} onClick={() => setActive(v)} data-testid={`video-card-${v.id}`}>
                <div className="video-thumb">
                  {v.thumbnail ? <img src={v.thumbnail} alt="" loading="lazy" /> : <div className="video-thumb-fallback" />}
                  <span className="video-play"><PlayCircle size={40} weight="fill" /></span>
                  {v.duration ? <span className="video-duration"><Clock size={11} /> {formatDuration(v.duration)}</span> : null}
                </div>
                <div className="p-3 text-left">
                  <p className="font-semibold text-sm" style={{ color: "var(--foreground)" }}>{v.title}</p>
                  {v.description && <p className="text-xs mt-1 line-clamp-2" style={{ color: "var(--muted)" }}>{v.description}</p>}
                  {v.access === "mentorship" && <span className="chip chip-muted mt-2 inline-flex items-center gap-1"><LockSimple size={10} /> Mentorship</span>}
                </div>
              </button>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

function Player({ video, onClose }) {
  const [src, setSrc] = useState("");
  const [err, setErr] = useState("");
  const [startAt, setStartAt] = useState(0);
  const frameRef = useRef(null);
  const posRef = useRef(0);

  useEffect(() => {
    setSrc(""); setErr(""); posRef.current = 0;
    api.get(`/resources/videos/${video.id}/playback`)
      .then(({ data }) => { setStartAt(data.start_at || 0); setSrc(data.iframe_url); })
      .catch((e) => setErr(e.response?.data?.detail || "This video can't be played right now."));
  }, [video.id]);

  // Vimeo player.js postMessage: subscribe to timeupdate once the player is ready, save position every ~10s.
  useEffect(() => {
    const VIMEO = "https://player.vimeo.com";
    const send = (method, value) => frameRef.current?.contentWindow?.postMessage(JSON.stringify({ method, value }), VIMEO);
    const onMsg = (e) => {
      if (e.origin !== VIMEO) return;
      try {
        const d = typeof e.data === "string" ? JSON.parse(e.data) : e.data;
        if (d?.event === "ready") send("addEventListener", "timeupdate");
        if (d?.event === "timeupdate" && typeof d.data?.seconds === "number") posRef.current = d.data.seconds;
      } catch { /* ignore */ }
    };
    window.addEventListener("message", onMsg);
    const save = () => { if (posRef.current > 0) api.put(`/resources/videos/${video.id}/progress`, { position: posRef.current }).catch(() => {}); };
    const t = setInterval(save, 10000);
    return () => { window.removeEventListener("message", onMsg); clearInterval(t); save(); };
  }, [video.id]);

  return (
    <section className="panel" data-testid="video-player-panel">
      <div className="panel-head">
        <div><p className="label-uppercase">Now playing</p><h2 className="panel-title">{video.title}</h2></div>
        <button className="btn btn-ghost btn-sm" onClick={onClose} data-testid="video-player-close">Close</button>
      </div>
      <div className="video-frame">
        {err ? <p className="error-banner m-4" data-testid="video-player-error">{err}</p>
          : src ? <iframe ref={frameRef} src={src} title={video.title} allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture; fullscreen" allowFullScreen data-testid="video-player-iframe" />
          : <div className="py-24 flex justify-center"><span className="spinner" /></div>}
      </div>
      {startAt > 10 && !err && <p className="text-xs px-4 pb-3" style={{ color: "var(--muted)" }} data-testid="video-resume-note">Resuming from {formatDuration(startAt) || `${startAt}s`}.</p>}
      {video.description && <p className="text-sm px-4 pb-4" style={{ color: "var(--muted)" }}>{video.description}</p>}
    </section>
  );
}
